// A filter chip (mockup 06: white pill with a chevron) and the menu it opens. The menu uses the
// ⋯ menu's look (mockup 03) and opens under the chip; chips that toggle have no menu.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDownIcon, TickIcon } from '../icons';

export interface MenuOption {
  value: string;
  label: string;
  /** A count or note on the right. */
  note?: string;
  /** Starts a new group with a small heading. */
  group?: string;
}

const CHIP =
  'flex h-9 shrink-0 items-center gap-1 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap';

export function chipClass(active: boolean): string {
  return `${CHIP} ${active ? 'border-accent bg-accent-tint text-accent-dark' : 'border-line bg-surface text-ink'}`;
}

/** A chip that switches something on or off. */
export function ToggleChip(props: { label: string; active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={props.active}
      onClick={props.onToggle}
      className={chipClass(props.active)}
    >
      {props.label}
    </button>
  );
}

/**
 * A chip with a menu. `multiple` keeps the menu open while you tick several options; otherwise
 * choosing one closes it. Choosing the selected option of a single choice clears it.
 */
export function ChipMenu(props: {
  label: string;
  /** What the chip says when something is chosen. */
  activeLabel?: string;
  name: string;
  options: MenuOption[];
  selected: readonly string[];
  multiple?: boolean;
  onChange: (selected: string[]) => void;
  /** Plain button style (the sort control), not a chip. */
  plain?: boolean;
  /** A single choice that always has a value (sort): no clearing. */
  required?: boolean;
  align?: 'left' | 'right';
  footer?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; left: number; width: number } | null>(null);
  const active = props.selected.length > 0 && !props.required;

  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const rect = button.current.getBoundingClientRect();
    const width = Math.min(260, window.innerWidth - 24);
    const left =
      props.align === 'right'
        ? Math.max(12, rect.right - width)
        : Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
    // Opens under the chip, moved up when it would run off the screen (the same height as the
    // menu's largest).
    const tallest = Math.min(420, window.innerHeight - 140);
    setAt({ top: Math.min(rect.bottom + 6, window.innerHeight - tallest - 12), left, width });
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, props.align]);

  const choose = (value: string) => {
    const on = props.selected.includes(value);
    if (props.multiple) {
      props.onChange(on ? props.selected.filter((v) => v !== value) : [...props.selected, value]);
    } else {
      props.onChange(on && !props.required ? [] : [value]);
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          props.plain
            ? 'flex h-8 items-center gap-1 rounded-btn border-0 bg-transparent px-2.5 text-[13px] font-semibold text-ink'
            : chipClass(active)
        }
      >
        {active && props.activeLabel ? props.activeLabel : props.label}
        <ChevronDownIcon />
      </button>
      {open && at && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            aria-label={props.name}
            className="fixed z-40 flex max-h-[min(420px,calc(100dvh-140px))] flex-col overflow-y-auto rounded-[16px] border border-line bg-surface p-2 shadow-[0_8px_24px_rgba(31,30,28,0.16)]"
            style={{ top: at.top, left: at.left, width: at.width }}
          >
            {props.options.map((o, i) => {
              const on = props.selected.includes(o.value);
              return (
                <div key={o.value}>
                  {o.group && (
                    <div
                      className={`px-3 pt-2 pb-1 text-[12px] font-semibold text-muted ${i > 0 ? 'mt-1 border-t border-line-soft' : ''}`}
                    >
                      {o.group}
                    </div>
                  )}
                  <button
                    type="button"
                    role={props.multiple ? 'menuitemcheckbox' : 'menuitemradio'}
                    aria-checked={on}
                    onClick={() => choose(o.value)}
                    className="flex min-h-11 w-full items-center gap-2 rounded-btn border-0 bg-transparent px-3 text-left text-[15px] font-medium text-ink hover:bg-bg"
                  >
                    <span className="flex w-[18px] shrink-0 text-accent">
                      {on ? <TickIcon /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    {o.note && <span className="text-[12px] text-muted">{o.note}</span>}
                  </button>
                </div>
              );
            })}
            {active && (
              <button
                type="button"
                onClick={() => {
                  props.onChange([]);
                  setOpen(false);
                }}
                className="mt-1 flex min-h-11 w-full items-center rounded-btn border-0 border-t border-line-soft bg-transparent px-3 text-left text-[14px] font-semibold text-accent"
              >
                Clear
              </button>
            )}
            {props.footer}
          </div>
        </>
      )}
    </>
  );
}
